import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Typography } from '@mui/material';
import {
  DataGridPro,
  DataGridProProps,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridRowId,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { isIOS, isMobile } from 'react-device-detect';

import { Province } from '../../../@types/KingdomResponse';
import { IntelTabCols } from '../../../@types/Models';
import {
  ColumnOrder,
  ColumnVisibility,
  KdKanban,
  KdTabColumnVisibility,
  Tab,
} from '../../../@types/State';
import { autosizeOptions } from '../../../Constants';
import { useIntelState } from '../../../context/IntelState';
import useCols from '../../../hooks/useCols';
import { useDataRefresher } from '../../../hooks/useDataRefresher';
import DetailPanelContent from '../components/DetailPanelContent';
import GridFooter from '../components/GridFooter';
import GridViewToolBarNew from '../components/GridViewToolBarNew';
import { StripedDataGrid } from '../components/StripedDataGrid';
import { enrichProvinces } from '../helpers/helpers';

declare module '@mui/x-data-grid-pro' {
  interface FooterPropsOverrides {
    dataSet: Province[] | undefined;
    hideTotals: boolean;
    zoom: number;
  }
}

const cellCss = {
  paddingLeft: '5px',
  paddingRight: '5px',
};

interface Props {
  currentTab: Tab;
  dataSet: Province[] | undefined;
  plan: KdKanban | undefined;
  isViewingEnemy: boolean;
  isViewingRecent?: boolean;
  hideTotals?: boolean;
  zoom: number;
}

const MainGrid: React.FC<Props> = ({
  currentTab,
  dataSet,
  plan,
  isViewingEnemy,
  isViewingRecent,
  hideTotals,
  zoom,
}) => {
  const { intelState, intelDispatch } = useIntelState();
  const apiRef = useGridApiRef();
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState(
    () => new Set<GridRowId>()
  );
  useDataRefresher(isViewingEnemy);
  const allCols: IntelTabCols | undefined = useCols(dataSet);
  const colSet = isViewingEnemy ? 'enemy' : 'own';
  const cols = allCols && allCols[colSet][currentTab];

  const getCols = () => {
    const columnVisibility: ColumnVisibility =
      intelState.columnVisibility[colSet][currentTab];
    if (columnVisibility) {
      if (isViewingRecent) {
        columnVisibility.location = true;
      }
      return columnVisibility;
    }
  };

  const rows = useMemo(() => {
    return enrichProvinces(plan, dataSet);
  }, [dataSet, plan]);

  useEffect(() => {
    if (currentTab && apiRef.current && cols) {
      apiRef.current.autosizeColumns &&
        apiRef.current.autosizeColumns(autosizeOptions);
    }
  }, [apiRef, cols, currentTab]);

  const handleColumnReorder = (a: GridColumnOrderChangeParams) => {
    const colReorder: ColumnOrder = {
      field: a.column.field,
      oldIndex: a.oldIndex,
      targetIndex: a.targetIndex,
    };
    if (colReorder.oldIndex === colReorder.targetIndex) return;
    intelDispatch({
      type: 'SET_TAB_COL_ORDER',
      payload: {
        colReorder,
        currentTab,
      },
    });
  };

  const hideDetailPanel = () => {
    setDetailPanelExpandedRowIds(new Set<GridRowId>());
  };

  const getDetailPanelContent: DataGridProProps['getDetailPanelContent'] = ({
    row,
  }) => (
    <DetailPanelContent
      row={row}
      hideDetailPanel={hideDetailPanel}
      isEnemy={isViewingEnemy}
    />
  );

  const handleDetailPanelExpandedRowIdsChange = useCallback<
    NonNullable<DataGridProProps['onDetailPanelExpandedRowIdsChange']>
  >(
    (newIds) => {
      const id = Array.from(newIds)[newIds.size - 1];
      if (newIds.size < 2) {
        setDetailPanelExpandedRowIds(newIds);
      } else {
        // this shouldnt happen, becasue the modal makes it impossible to open multiple rows at the same time
        const newSet = new Set<GridRowId>();
        const newIdsArray = Array.from(newIds);
        newSet.add(newIdsArray[newIdsArray.length - 1]);
        setDetailPanelExpandedRowIds(newSet);
      }
      if (isViewingEnemy && intelState.enemyPlan) {
        let itemWasSet = false;
        for (const col of intelState.enemyPlan.columns) {
          if (itemWasSet) break;
          for (const item of col.items) {
            if (item.province.id === id) {
              intelDispatch({
                type: 'SET_SELECTED_KANBAN_PROVINCE_ID',
                payload: item.id,
              });
              itemWasSet = true;
              break;
            }
          }
          if (itemWasSet) break;
        }
      }
    },
    [intelDispatch, intelState.enemyPlan, isViewingEnemy]
  );

  const handleColVisibilityChange = (newModel: GridColumnVisibilityModel) => {
    intelDispatch({
      type: 'SET_TAB_COL_VISIBILITY',
      payload: {
        newModel,
        currentTab,
        colSet,
      },
    });
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }
    const fileReader = new FileReader();
    fileReader.readAsText(e.target.files[0], 'UTF-8');
    fileReader.onload = (e) => {
      if (e.target?.result && typeof e.target.result === 'string') {
        const json: KdTabColumnVisibility = JSON.parse(e.target.result);
        const newModel = json[colSet][currentTab];
        intelDispatch({
          type: 'SET_TAB_COL_VISIBILITY',
          payload: {
            newModel,
            currentTab,
            colSet,
          },
        });
      }
    };
  };

  return rows && cols ? (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <StripedDataGrid
        rows={rows}
        apiRef={apiRef}
        initialState={{
          pinnedColumns: isMobile
            ? { left: ['slot'] }
            : { left: ['slot', 'name'] },
        }}
        autosizeOptions={autosizeOptions}
        autosizeOnMount={true}
        columnVisibilityModel={getCols()}
        onColumnOrderChange={handleColumnReorder}
        onColumnVisibilityModelChange={handleColVisibilityChange}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={({ row }) => 'auto'}
        detailPanelExpandedRowIds={detailPanelExpandedRowIds}
        onDetailPanelExpandedRowIdsChange={
          handleDetailPanelExpandedRowIdsChange
        }
        columns={cols}
        density='compact'
        showToolbar
        slots={{
          toolbar: GridViewToolBarNew,
          // columnSortedAscendingIcon: () => null,
          // columnSortedDescendingIcon: () => null,
          columnMenuUnsortIcon: null,
          footer: GridFooter,
        }}
        slotProps={{
          toolbar: {
            isViewingEnemy,
            handleFileUpload,
            isViewingRecent,
          },
          footer: { dataSet, hideTotals, zoom },
        }}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        loading={intelState.loading}
        hideFooterPagination
        disableColumnMenu={isMobile}
        rowHeight={40}
        sx={{
          fontSize: `${isIOS ? zoom : 100}%`,
          '& .MuiDataGrid-columnHeader': cellCss,
          '& .MuiDataGrid-cell': cellCss,
          '& .spell-col': {
            paddingLeft: '2px',
            paddingRight: '2px',
          },
          '& .toggle-col': {
            paddingLeft: '2px',
            paddingRight: '2px',
            minWidth: '34px !important',
            maxWidth: '34px !important',
          },
          // [`& .${gridClasses.columnSeparator}`]: {
          //   display: zoom < 100 ? 'none' : 'flex',
          // },
        }}
        disableVirtualization={isMobile}
      />
    </div>
  ) : (
    <Typography variant='body1' sx={{ marginTop: 3 }}>
      No Kingdom Data found
    </Typography>
  );
};

export default MainGrid;
